import { useMemo } from 'react'
import ReactApexChart from 'react-apexcharts'
import { ApexOptions } from 'apexcharts'
import { baseChart, tightGrid } from '../ChartFrame'
import { LiveChartFrame } from './LiveChartFrame'
import { CardShell } from './CardShell'
import { EventsResponse, EventsState, Resource } from './types'
import { ChartKind } from './sections'
import { SERIES, themed, useChartTheme } from './chartTheme'
import { tooltipHtml } from './tooltip'
import { MsgId, useFormat, useT } from './i18n'

/** Most recent event days shown in the chart. */
const MAX_EVENTS_SHOWN = 12

const STATE_MESSAGE: Record<Exclude<EventsState, 'ok'>, MsgId> = {
  no_events_for_site: 'PROFILES_DASHBOARD.EVENTS.STATE_NO_SITE',
  no_events_in_period: 'PROFILES_DASHBOARD.EVENTS.STATE_NO_PERIOD',
  no_comparable_event_days: 'PROFILES_DASHBOARD.EVENTS.STATE_NO_COMPARABLE',
}

export function EventsCard({
  events,
  chartType,
}: {
  events: Resource<EventsResponse>
  chartType: ChartKind
}) {
  const t = useT()
  return (
    <CardShell
      title={t('PROFILES_DASHBOARD.SECTION.EVENTS')}
      subtitle={t('PROFILES_DASHBOARD.EVENTS.SUBTITLE')}
      state={events}
      empty={(e) => (e.state === 'ok' ? null : t(STATE_MESSAGE[e.state]))}
      render={(e) => <EventsChart data={e} chartType={chartType} />}
    />
  )
}

function EventsChart({ data, chartType }: { data: EventsResponse; chartType: ChartKind }) {
  const theme = useChartTheme()
  const t = useT()
  const f = useFormat()
  const items = data.impact.items.slice(-MAX_EVENTS_SHOWN)
  const kind: 'bar' | 'line' | 'area' = chartType
  const options: ApexOptions = useMemo(
    () =>
      themed(
        {
          chart: { ...baseChart, type: kind, toolbar: { show: false } },
          grid: tightGrid,
          plotOptions: { bar: { columnWidth: '45%', borderRadius: 4 } },
          stroke: { curve: 'straight', width: kind === 'bar' ? 0 : 2.5 },
          // dark text on the yellow bars: white would be unreadable
          dataLabels: {
            enabled: kind === 'bar',
            style: { colors: [theme.onBright] },
            formatter: (v) => `${f.number(Number(v), 2)}×`,
          },
          xaxis: {
            categories: items.map((i) => f.shortDate(i.date)),
            labels: { rotate: -35, hideOverlappingLabels: true, trim: false },
          },
          yaxis: {
            title: { text: t('PROFILES_DASHBOARD.EVENTS.Y_AXIS') },
            min: 0,
            forceNiceScale: true,
            labels: { formatter: (v) => f.number(v, 2) },
          },
          colors: [SERIES.yellow],
          legend: { show: false },
          annotations: { yaxis: [{ y: 1, borderColor: theme.muted, strokeDashArray: 4 }] },
          tooltip: {
            shared: false,
            intersect: false,
            custom: ({ dataPointIndex }: any) => {
              const i = items[dataPointIndex]
              if (!i) return ''
              return tooltipHtml(theme, f.date(i.date), [
                {
                  label: t('PROFILES_DASHBOARD.EVENTS.TIP_EVENT_DAY'),
                  value: f.kwh(i.total),
                  color: SERIES.yellow,
                },
                { label: t('PROFILES_DASHBOARD.EVENTS.TIP_NORMAL_DAY'), value: f.kwh(i.baseline) },
                {
                  label: t('PROFILES_DASHBOARD.EVENTS.TIP_RATIO'),
                  value: `${f.number(i.ratio, 2)}×`,
                },
                {
                  label: t('PROFILES_DASHBOARD.EVENTS.TIP_EVENT'),
                  value: i.types.join(', ') || '—',
                },
              ])
            },
          },
        },
        theme
      ),
    [items, kind, theme, t, f]
  )
  return (
    <>
      <div className='d-flex flex-wrap gap-2 mb-3'>
        <span className='badge badge-light-primary'>
          {t('PROFILES_DASHBOARD.EVENTS.BADGE_DAYS', { count: data.impact.event_days })}
        </span>
        <span className='badge badge-light'>
          {t('PROFILES_DASHBOARD.EVENTS.BADGE_UPLIFT', {
            value: f.percent(data.impact.uplift_pct),
          })}
        </span>
        {data.impact.by_type.map((type) => (
          <span className='badge badge-light' key={type.type}>
            {t('PROFILES_DASHBOARD.EVENTS.BADGE_TYPE', {
              type: type.type,
              ratio: f.number(type.mean_ratio, 2),
              count: type.days,
            })}
          </span>
        ))}
      </div>
      <LiveChartFrame>
        <ReactApexChart
          key={`events-${data.meta.site.id}-${data.meta.start}-${data.meta.end}-${kind}-${theme.mode}-${f.locale}`}
          options={options}
          series={[
            { name: t('PROFILES_DASHBOARD.EVENTS.Y_AXIS'), data: items.map((i) => i.ratio) },
          ]}
          type={kind}
          width='100%'
          height={300}
        />
      </LiveChartFrame>
      {data.impact.items.length > MAX_EVENTS_SHOWN ? (
        <div className='fs-8 text-muted'>
          {t('PROFILES_DASHBOARD.EVENTS.FOOTNOTE', {
            shown: MAX_EVENTS_SHOWN,
            total: data.impact.items.length,
          })}
        </div>
      ) : null}
    </>
  )
}
